import { Immutable } from "immer";
import React, { useEffect } from "react";
import SVG from "react-inlinesvg";
import { c, t } from "ttag";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

import iconInfo from "../../../../img/icons/info.svg";
import { ErrorBoundary } from "../../../components/ErrorBoundary";
import { MAX_BASKET_LINE_QUANTITY } from "../../../constants";
import { IBasket, IBasketLine } from "../../../models/catalogue";
import { FinancingPlan } from "../../../models/financing";
import {
    IBasketLineAPIURL,
    IProductAPIURL,
    isoMonths,
} from "../../../models/nominals";
import {
    format,
    formatDateString,
    formatTermLength,
} from "../../../utils/format";
import { getDinero, getMonthlyPrice, sum } from "../../../utils/money";
import { PredictedDeliveryDate } from "../PredictedDeliveryDate";
import { PreferredDeliveryDateHelpText } from "../PreferredDeliveryDateHelpText";
import {
    BasketLineVariant,
    PredictedDeliveryDateDisplayType,
    PredictedDeliveryDateStyle,
    PreferredDeliveryDateDisplayType,
} from "../constants";
import { getBasketLineAdvertisingContent } from "../selectors";
import { getLongestFinancingPlan } from "../utils";
import { BasketLineImage } from "./BasketLineImage";
import { BasketLineOptions } from "./BasketLineOptions";
import { BasketLinePrice } from "./BasketLinePrice";
import { BasketLineTitle } from "./BasketLineTitle";
import { BasketLineUpsellMessage } from "./BasketLineUpsellMessage";
import { BasketLineValueProps } from "./BasketLineValueProps";
import { TGetProductUpsell } from "./utils";

import styles from "./BasketLineFull.module.scss";

const RemoveButton = (
    props: Immutable<{
        onRemoveLine: (event?: React.MouseEvent<HTMLElement>) => void;
    }>,
) => (
    <button
        onClick={props.onRemoveLine}
        className={`${styles.removeButton} al-basket-line__button--remove`}
    >
        {t`[Remove from cart]`}
    </button>
);

const QuantitySelector = (
    props: Immutable<{
        line: IBasketLine;
        onUpdateLineQuantity: (
            event: React.FormEvent<HTMLSelectElement>,
        ) => void;
    }>,
): React.ReactNode => {
    const line = props.line;
    if (!line.quantity) {
        return null;
    }
    const options = [];
    for (let i = 0; i <= MAX_BASKET_LINE_QUANTITY; i++) {
        options.push(
            <option key={i} value={i}>
                {i}
            </option>,
        );
    }
    return (
        <div className={styles.quantity}>
            <label htmlFor="product_quantity">{t`Qty:`} &nbsp;</label>
            <select
                value={line.quantity}
                onChange={props.onUpdateLineQuantity}
                id="product_quantity"
            >
                {options}
            </select>
        </div>
    );
};

const ModificationLog = (
    props: Immutable<{ line: IBasketLine }>,
): React.ReactNode => {
    const line = props.line;
    if (!line.quantity) {
        return null;
    }
    const logEntries = line.modification_log_entries.filter((entry) => {
        return (
            entry.user_is_staff && // User must be a staff user
            !!entry.user_name
        ); // Don't display anything added by a user without a name
    });
    if (logEntries.length <= 0) {
        return null;
    }
    const logEntry = logEntries[logEntries.length - 1];
    return (
        <div className={styles.modificationMessage}>
            <SVG className={styles.modificationIcon} src={iconInfo} />
            <span>
                {t`This item was added/modified on your behalf by ${logEntry.user_name}.`}
            </span>
            <span className={styles.modificationTimestamp}>
                {formatDateString(logEntry.action_time, "MM/dd/yyyy h:mm a zz")}
            </span>
        </div>
    );
};

const onFinePrintClick = (event: React.MouseEvent<HTMLElement>) => {
    if (event) {
        event.preventDefault();
    }
    const document = getDocument();
    if (!document) {
        return;
    }

    const finePrint = document.querySelector("#basket-lines__fine-print");
    finePrint?.scrollIntoView({ behavior: "smooth" });
};

const Financing = (
    props: Immutable<{
        basket: IBasket;
        line: IBasketLine;
        financingPlans: readonly FinancingPlan[];
    }>,
): React.ReactNode => {
    // Find all of the lines which are children of this line
    const childLines = props.basket.lines.filter(
        (_line) => _line.parent === props.line.id,
    );
    // If any child lines exist, then this line's price is the sum of those
    // children. Else, just use the single standalone line.
    const lines = childLines.length > 0 ? childLines : [props.line];

    const longestActivePlan = getLongestFinancingPlan(props.financingPlans);
    const termLength = longestActivePlan
        ? longestActivePlan.term_months
        : props.line.product.price.per_month_term_length || isoMonths.wrap(0);
    const linePrice = sum(lines.map((line) => getDinero(line.price_excl_tax)));
    const showMonthlyPricing = !!(
        props.line.product.price.enable_per_month_pricing && termLength
    );
    if (!showMonthlyPricing) {
        return null;
    }
    const monthlyPrice = getMonthlyPrice(
        sum(lines.map((line) => getDinero(line.price_excl_tax))),
        termLength,
    );
    const formattedPrice = format.money(linePrice);
    return (
        <div className={styles.financing}>
            {/* TODO(i18n): Use jt to keep complete sentence with interpolated components: jt`or ${monthlyLink}/mo. for ${termLink} mos.` */}
            <button
                className={styles.financingButton}
                onClick={onFinePrintClick}
            >
                or{" "}
                <span className={styles.underlined}>
                    {format.money(monthlyPrice)}/mo.
                </span>
                <sup>2</sup> for{" "}
                <span className={styles.underlined}>
                    {formatTermLength(termLength)} mos.
                </span>
                {longestActivePlan && (
                    <sup>{longestActivePlan.fine_print_superscript}</sup>
                )}
            </button>
            <br />
            {c("basket-line-financing-payments")
                .t`(total payments of ${formattedPrice})`}
        </div>
    );
};

const ValueProps = (
    props: Immutable<{
        line: IBasketLine;
        isMobile: boolean;
    }>,
): React.ReactNode => {
    if (!props.line.product.value_props) {
        return null;
    }
    return (
        <BasketLineValueProps
            isMobile={props.isMobile}
            valueProps={props.line.product.value_props}
        />
    );
};

type IProps = Immutable<{
    line: IBasketLine;
    basket: IBasket;
    variant: BasketLineVariant;
    financingPlans: readonly FinancingPlan[];
    getProductUpsell: TGetProductUpsell;
    showValueProps: boolean;
    predictedDeliveryDates: PredictedDeliveryDateDisplayType;
    preferredDeliveryDates: PreferredDeliveryDateDisplayType;
    loadConcreteBundles: (url: IProductAPIURL) => void;
    onRemoveLine: (event?: React.MouseEvent<HTMLElement>) => void;
    onUpdateLineQuantity: (event: React.FormEvent<HTMLSelectElement>) => void;
    isMobile: boolean;
    deliveryIsFree?: boolean;
}> & {
    addBasketLine: (
        productURL: IProductAPIURL,
        qty?: number,
    ) => Promise<IBasket>;
    removeBasketLine: (lineURL: IBasketLineAPIURL) => Promise<IBasket>;
    updateBasketLineQuantity: (
        lineURL: IBasketLineAPIURL,
        newQty: number,
    ) => Promise<IBasket>;
};

export const BasketLineFull = (props: IProps): React.ReactNode => {
    useEffect(() => {
        props.loadConcreteBundles(props.line.product.url);
    }, [props.line.product.url]);

    const line = props.line;
    const product = line.product;
    const upsell = props.getProductUpsell(
        props.basket,
        props.line,
        props.addBasketLine,
    );
    const advertisingContent = getBasketLineAdvertisingContent(
        props.basket,
        props.line,
    );
    const valueProps = (
        <ErrorBoundary>
            <ValueProps line={props.line} isMobile={props.isMobile} />
        </ErrorBoundary>
    );
    return (
        <li>
            <section
                className={styles.basketLineFull}
                role="group"
                aria-label={t`product`}
            >
                <div className={styles.firstColumn}>
                    <BasketLineImage product={product} />
                    {props.showValueProps && !props.isMobile && valueProps}
                </div>
                <div>
                    <BasketLineTitle product={product} />
                    <BasketLineOptions product={product} />
                </div>
                <QuantitySelector
                    line={props.line}
                    onUpdateLineQuantity={props.onUpdateLineQuantity}
                />
                <div>
                    {line.price_excl_tax_excl_discounts && (
                        <BasketLinePrice
                            line={line}
                            basket={props.basket}
                            isMinimal={false}
                            variant={props.variant}
                        />
                    )}
                    {line.price_excl_tax_excl_discounts && (
                        <Financing
                            basket={props.basket}
                            line={props.line}
                            financingPlans={props.financingPlans}
                        />
                    )}
                    {props.showValueProps && props.isMobile && valueProps}
                </div>
                {props.line.product.enable_predicted_delivery_date && (
                    <div className={styles.whiteGloveDelivery}>
                        <PredictedDeliveryDate
                            type={props.predictedDeliveryDates}
                            style={PredictedDeliveryDateStyle.ONE_LINE}
                            deliveryIsFree={props.deliveryIsFree}
                        />
                        <PreferredDeliveryDateHelpText
                            type={props.preferredDeliveryDates}
                        />
                    </div>
                )}
                {advertisingContent.map((content) => (
                    <BasketLineUpsellMessage
                        key={`${content.group_priority}-${content.offer_priority}`}
                        content={content}
                    />
                ))}
                <ModificationLog line={props.line} />
                <RemoveButton onRemoveLine={props.onRemoveLine} />
                <ErrorBoundary>
                    <div className={styles.basketLineSuggestions}>
                        {upsell?.content}
                    </div>
                </ErrorBoundary>
            </section>
        </li>
    );
};
